import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  CloseButton,
} from "@headlessui/react";
import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { Button } from "@/ui-components/button-with-loading";
import { useDensityOptional } from "@/contexts/DensityContext";
import clsx from "clsx";
import { getAttribution, getAttributionQueryParams } from "@/utils/attribution";
import { getClickIds } from "@/utils/clickIds";
import { trackSignupIntent } from "@/utils/gtm";

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type BillingCycle = "monthly" | "annually";
type BasicVariant = "forex" | "energy" | "metals";
type Currency = "usd" | "gbp" | "eur";

const PRICING = {
  basic: {
    usd: { monthly: 199, annually: 1990 },
    gbp: { monthly: 175, annually: 1750 },
    eur: { monthly: 199, annually: 1990 },
  },
  pro: {
    usd: { monthly: 399, annually: 3990 },
    gbp: { monthly: 350, annually: 3500 },
    eur: { monthly: 399, annually: 3990 },
  },
  trial: {
    usd: 24.99,
    gbp: 24.99,
    eur: 29.99,
  },
};

const CURRENCY_SYMBOLS: Record<Currency, string> = {
  usd: "$",
  gbp: "£",
  eur: "€",
};

const CHARGEBEE_CHECKOUT_BASE = "https://newsquawk.chargebee.com/hosted_pages/checkout";

const CHARGEBEE_ITEM_PRICE_IDS = {
  basic: {
    forex: {
      usd: { monthly: "Newsquawk---Basic---FX-USD-Monthly", annually: "Newsquawk---Basic---FX-USD-Yearly" },
      gbp: { monthly: "Newsquawk---Basic---FX-GBP-Monthly", annually: "Newsquawk---Basic---FX-GBP-Yearly" },
      eur: { monthly: "Newsquawk---Basic---FX-EUR-Monthly", annually: "Newsquawk---Basic---FX-EUR-Yearly" },
    },
    energy: {
      usd: { monthly: "Newsquawk---Basic---Energy-USD-Monthly", annually: "Newsquawk---Basic---Energy-USD-Yearly" },
      gbp: { monthly: "Newsquawk---Basic---Energy-GBP-Monthly", annually: "Newsquawk---Basic---Energy-GBP-Yearly" },
      eur: { monthly: "Newsquawk---Basic---Energy-EUR-Monthly", annually: "Newsquawk---Basic---Energy-EUR-Yearly" },
    },
    metals: {
      usd: { monthly: "Newsquawk---Basic---Metals-USD-Monthly", annually: "Newsquawk---Basic---Metals-USD-Yearly" },
      gbp: { monthly: "Newsquawk---Basic---Metals-GBP-Monthly", annually: "Newsquawk---Basic---Metals-GBP-Yearly" },
      eur: { monthly: "Newsquawk---Basic---Metals-EUR-Monthly", annually: "Newsquawk---Basic---Metals-EUR-Yearly" },
    },
  },
  pro: {
    usd: { monthly: "Newsquawk-Pro-USD-Monthly", annually: "Newsquawk-Pro-USD-Yearly" },
    gbp: { monthly: "Newsquawk-Pro-GBP-Monthly", annually: "Newsquawk-Pro-GBP-Yearly" },
    eur: { monthly: "Newsquawk-Pro-EUR-Monthly", annually: "Newsquawk-Pro-EUR-Yearly" },
  },
  trial: {
    usd: "Newsquawk-Pro-With-Trial-USD-Monthly",
    gbp: "Newsquawk-Pro-With-Trial-GBP-Monthly",
    eur: "Newsquawk-Pro-With-Trial-EUR-Monthly",
  },
} as const;

/**
 * Builds Chargebee custom-field params (`cf_*`) carrying ad-platform click
 * IDs and the original referring website. These map onto Subscription
 * custom fields in Chargebee — fields that don't exist in Chargebee admin
 * are silently dropped at checkout, so wiring them here is safe before the
 * fields are created.
 */
function getCheckoutCustomFieldParams(): string {
  const clickIds = getClickIds();
  const referringWebsite = getAttribution()?.referrer_domain;
  const fields: Record<string, string | undefined> = {
    cf_gclid: clickIds.gclid,
    cf_fbclid: clickIds.fbclid,
    cf_li_fat_id: clickIds.li_fat_id,
    cf_referring_website: referringWebsite,
  };
  const parts: string[] = [];
  for (const [key, value] of Object.entries(fields)) {
    if (value) parts.push(`${key}=${encodeURIComponent(value)}`);
  }
  return parts.length > 0 ? "&" + parts.join("&") : "";
}

function getBasicCheckoutUrl(variant: BasicVariant, currency: Currency, cycle: BillingCycle): string {
  const itemPriceId = CHARGEBEE_ITEM_PRICE_IDS.basic[variant][currency][cycle];
  // Forward stored attribution or fall back to portal-demo as default source
  const attrParams = getAttributionQueryParams() || "&utm_source=portal-demo";
  const cfParams = getCheckoutCustomFieldParams();
  return `${CHARGEBEE_CHECKOUT_BASE}?subscription_items[item_price_id][0]=${itemPriceId}${attrParams}${cfParams}`;
}

function getTrialCheckoutUrl(currency: Currency): string {
  const itemPriceId = CHARGEBEE_ITEM_PRICE_IDS.trial[currency];
  const attrParams = getAttributionQueryParams() || "&utm_source=portal-demo";
  const cfParams = getCheckoutCustomFieldParams();
  return `${CHARGEBEE_CHECKOUT_BASE}?subscription_items[item_price_id][0]=${itemPriceId}${attrParams}${cfParams}`;
}

const BASIC_FEATURES = [
  "Single asset class squawk",
  "Newsquawk Headlines Feed",
  "Newsquawk Watchlists",
  "Newsquawk Research Sheets",
  "Newsquawk Economic Calendar",
  "Newsquawk Mobile App",
  "24/7 Online Support",
  "Social & Web News Feeds inc. Trump Truths",
];

const PRO_FEATURES = [
  "All channels including Multi-Asset",
  "Equities & Fixed Income Channels",
  "All Basic features included",
  "24/7 Premium Priority Support",
  "Premium Equities Suite",
  "Broker Ratings Feed",
  "Benzinga, The Fly & More",
  "Smart SEC Filings Feed",
  "Option Activity Tracking",
  "Insider & Gov Trades",
];

const GBP_TIMEZONES = new Set([
  "Europe/London",
  "Europe/Jersey",
  "Europe/Guernsey",
  "Europe/Isle_of_Man",
]);

function detectCurrency(): Currency {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (timeZone) {
      if (GBP_TIMEZONES.has(timeZone)) {
        return "gbp";
      }

      if (
        timeZone.startsWith("Europe/") ||
        timeZone === "Atlantic/Canary" ||
        timeZone === "Atlantic/Madeira"
      ) {
        return "eur";
      }

      return "usd";
    }
  } catch {
    // Intl API unavailable, fall through to language-based fallback
  }

  const locale = (navigator.language || "en-US").toLowerCase();

  if (locale.startsWith("en-gb")) {
    return "gbp";
  }

  const euLanguages = ["de", "fr", "es", "it", "nl", "pt", "el", "fi"];
  if (euLanguages.includes(locale.split("-")[0])) {
    return "eur";
  }

  return "usd";
}

function formatPrice(
  amount: number,
  currency: Currency,
  cycle: BillingCycle
): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  const suffix = cycle === "monthly" ? "/month" : "/year";
  return `${symbol}${amount.toLocaleString()}${suffix}`;
}

export function PricingModal({ isOpen, onClose }: PricingModalProps) {
  const { isCompact } = useDensityOptional();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("monthly");
  const [basicVariant, setBasicVariant] = useState<BasicVariant>("forex");
  const currency = useMemo(() => detectCurrency(), []);

  const basicPrice = PRICING.basic[currency][billingCycle];
  const proPrice = PRICING.pro[currency][billingCycle];
  const trialPrice = PRICING.trial[currency];

  // Fire signup_intent when modal opens
  useEffect(() => {
    if (isOpen) {
      const attr = getAttribution();
      trackSignupIntent({
        intent_type: "pricing_modal_opened",
        utm_source: attr?.utm_source,
        utm_medium: attr?.utm_medium,
        utm_campaign: attr?.utm_campaign,
        referrer_domain: attr?.referrer_domain,
      });
    }
  }, [isOpen]);

  const handleCheckoutClick = (planType: string, planVariant?: string) => {
    const attr = getAttribution();
    trackSignupIntent({
      intent_type: "checkout_clicked",
      plan_type: planType,
      plan_variant: planVariant,
      billing_cycle: billingCycle,
      utm_source: attr?.utm_source,
      utm_medium: attr?.utm_medium,
      utm_campaign: attr?.utm_campaign,
      referrer_domain: attr?.referrer_domain,
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/95 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-5xl transform overflow-hidden rounded-2xl bg-gray-900 shadow-2xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* Gradient background effect */}
            <svg
              viewBox="0 0 1208 1024"
              className="absolute -top-40 left-1/2 -z-10 h-[64rem] -translate-x-1/2 opacity-30 [mask-image:radial-gradient(closest-side,white,transparent)]"
              aria-hidden="true"
            >
              <ellipse
                cx={604}
                cy={512}
                rx={604}
                ry={512}
                fill="url(#pricing-gradient)"
              />
              <defs>
                <radialGradient id="pricing-gradient">
                  <stop stopColor="#00aeef" />
                  <stop offset={1} stopColor="#0037ef" />
                </radialGradient>
              </defs>
            </svg>

            {/* Close button */}
            <CloseButton
              as={Button}
              variant="secondary"
              className="absolute right-4 top-4 z-10 p-1.5 bg-gray-800/50 hover:bg-gray-700 border-0"
              onClick={onClose}
            >
              <XMarkIcon className="h-5 w-5 text-gray-400" />
            </CloseButton>

            <div className={clsx(
              "relative",
              isCompact ? "px-4 py-4 sm:px-6 sm:py-6" : "px-6 py-10 sm:px-10 sm:py-14"
            )}>
              {/* Header */}
              <div className={clsx("text-center", isCompact ? "mb-4" : "mb-10")}>
                <h2 className={clsx(
                  "font-semibold text-squawk-blue uppercase tracking-wide",
                  isCompact ? "text-xs" : "text-sm"
                )}>
                  Pricing
                </h2>
                <p className={clsx(
                  "mt-2 font-bold tracking-tight text-white",
                  isCompact ? "text-xl sm:text-2xl" : "text-3xl sm:text-4xl"
                )}>
                  Choose your plan
                </p>
                {!isCompact && (
                  <p className="mt-4 text-base text-gray-400 max-w-2xl mx-auto">
                    Access real-time market intelligence with institutional-grade
                    news and analysis
                  </p>
                )}
              </div>

              {/* Billing cycle toggle */}
              <div className={clsx("flex justify-center", isCompact ? "mb-4" : "mb-10")}>
                <div className={clsx(
                  "grid grid-cols-2 gap-x-1 rounded-full p-1 text-center font-semibold ring-1 ring-inset ring-white/10 bg-gray-800/50",
                  isCompact ? "text-xs" : "text-sm"
                )}>
                  <button
                    type="button"
                    onClick={() => setBillingCycle("monthly")}
                    className={clsx(
                      "rounded-full transition-all",
                      isCompact ? "px-3 py-1.5" : "px-4 py-2",
                      billingCycle === "monthly"
                        ? "bg-squawk-blue text-white"
                        : "text-gray-400 hover:text-white"
                    )}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle("annually")}
                    className={clsx(
                      "rounded-full transition-all",
                      isCompact ? "px-3 py-1.5" : "px-4 py-2",
                      billingCycle === "annually"
                        ? "bg-squawk-blue text-white"
                        : "text-gray-400 hover:text-white"
                    )}
                  >
                    Annually
                  </button>
                </div>
              </div>

              {/* Pricing cards */}
              <div className={clsx(
                "grid grid-cols-1 lg:grid-cols-2 max-w-4xl mx-auto",
                isCompact ? "gap-4 lg:gap-5" : "gap-6 lg:gap-8"
              )}>
                {/* Basic Card */}
                <div className={clsx(
                  "rounded-2xl bg-gray-800/60 ring-1 ring-white/10 flex flex-col",
                  isCompact ? "p-4 sm:p-5" : "p-6 sm:p-8"
                )}>
                  <div>
                    <h3 className={clsx(
                      "font-semibold text-white",
                      isCompact ? "text-base" : "text-lg"
                    )}>
                      Newsquawk Basic
                    </h3>
                    {!isCompact && (
                      <p className="mt-2 text-sm text-gray-400">
                        Single asset class coverage for focused traders
                      </p>
                    )}

                    {/* Variant selector */}
                    <div className={clsx(isCompact ? "mt-2" : "mt-4")}>
                      <div className={clsx(
                        "grid grid-cols-3 gap-1 rounded-lg p-1 text-center font-medium ring-1 ring-inset ring-white/10 bg-gray-900/50",
                        isCompact ? "text-2xs" : "text-xs"
                      )}>
                        {(["forex", "energy", "metals"] as BasicVariant[]).map(
                          (variant) => (
                            <button
                              key={variant}
                              type="button"
                              onClick={() => setBasicVariant(variant)}
                              className={clsx(
                                "rounded-md capitalize transition-all",
                                isCompact ? "px-1.5 py-1" : "px-2 py-1.5",
                                basicVariant === variant
                                  ? "bg-gray-700 text-white"
                                  : "text-gray-400 hover:text-white"
                              )}
                            >
                              {variant}
                            </button>
                          )
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <p className={clsx(
                      "flex items-baseline gap-x-1",
                      isCompact ? "mt-3" : "mt-6"
                    )}>
                      <span className={clsx(
                        "font-bold tracking-tight text-white",
                        isCompact ? "text-2xl" : "text-4xl"
                      )}>
                        {CURRENCY_SYMBOLS[currency]}
                        {basicPrice.toLocaleString()}
                      </span>
                      <span className={clsx(
                        "font-semibold text-gray-400",
                        isCompact ? "text-xs" : "text-sm"
                      )}>
                        {billingCycle === "monthly" ? "/month" : "/year"}
                      </span>
                    </p>

                    {/* Features */}
                    <ul className={clsx(
                      "text-gray-300",
                      isCompact ? "mt-4 space-y-1.5 text-xs" : "mt-8 space-y-3 text-sm"
                    )}>
                      {BASIC_FEATURES.map((feature) => (
                        <li key={feature} className="flex gap-x-2">
                          <CheckIcon
                            className={clsx(
                              "flex-none text-squawk-blue",
                              isCompact ? "h-4 w-4" : "h-5 w-5"
                            )}
                            aria-hidden="true"
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <a
                    href={getBasicCheckoutUrl(basicVariant, currency, billingCycle)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleCheckoutClick("basic", basicVariant)}
                    className={clsx(
                      "block w-full rounded-lg bg-white/10 text-center font-semibold text-white ring-1 ring-inset ring-white/10 hover:bg-white/20 transition-colors capitalize",
                      isCompact ? "mt-4 px-3 py-2 text-xs" : "mt-8 px-4 py-3 text-sm"
                    )}
                  >
                    Get Basic — {basicVariant}
                  </a>
                </div>

                {/* Pro Card (Featured) */}
                <div className={clsx(
                  "rounded-2xl bg-gray-800/60 ring-2 ring-squawk-blue flex flex-col relative",
                  isCompact ? "p-4 sm:p-5" : "p-6 sm:p-8"
                )}>
                  {/* Popular badge */}
                  <div className="absolute -top-3 right-6">
                    <span className={clsx(
                      "inline-flex items-center rounded-full bg-squawk-blue font-semibold text-white",
                      isCompact ? "px-2 py-0.5 text-2xs" : "px-3 py-1 text-xs"
                    )}>
                      Most popular
                    </span>
                  </div>

                  <div>
                    <h3 className={clsx(
                      "font-semibold text-squawk-blue",
                      isCompact ? "text-base" : "text-lg"
                    )}>
                      Newsquawk Pro
                    </h3>
                    {!isCompact && (
                      <p className="mt-2 text-sm text-gray-400">
                        Complete multi-asset coverage with premium features
                      </p>
                    )}

                    {/* Price */}
                    <p className={clsx(
                      "flex items-baseline gap-x-1",
                      isCompact ? "mt-3" : "mt-6"
                    )}>
                      <span className={clsx(
                        "font-bold tracking-tight text-white",
                        isCompact ? "text-2xl" : "text-4xl"
                      )}>
                        {CURRENCY_SYMBOLS[currency]}
                        {proPrice.toLocaleString()}
                      </span>
                      <span className={clsx(
                        "font-semibold text-gray-400",
                        isCompact ? "text-xs" : "text-sm"
                      )}>
                        {billingCycle === "monthly" ? "/month" : "/year"}
                      </span>
                    </p>

                    {/* Features */}
                    <ul className={clsx(
                      "text-gray-300",
                      isCompact ? "mt-4 space-y-1.5 text-xs" : "mt-8 space-y-3 text-sm"
                    )}>
                      {PRO_FEATURES.map((feature) => (
                        <li key={feature} className="flex gap-x-2">
                          <CheckIcon
                            className={clsx(
                              "flex-none text-squawk-blue",
                              isCompact ? "h-4 w-4" : "h-5 w-5"
                            )}
                            aria-hidden="true"
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA */}
                  <div className={isCompact ? "mt-4" : "mt-8"}>
                    <a
                      href={getTrialCheckoutUrl(currency)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleCheckoutClick("pro_trial")}
                      className={clsx(
                        "block w-full rounded-lg bg-squawk-blue text-center font-semibold text-white hover:bg-squawk-blue/80 transition-colors",
                        isCompact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm"
                      )}
                    >
                      Try Pro — 7 Days for {CURRENCY_SYMBOLS[currency]}{trialPrice}
                    </a>
                    <p className={clsx(
                      "text-center text-gray-500",
                      isCompact ? "mt-1.5 text-2xs" : "mt-2 text-xs"
                    )}>
                      then {CURRENCY_SYMBOLS[currency]}{proPrice.toLocaleString()}{billingCycle === "monthly" ? "/month" : "/year"} · cancel anytime
                    </p>
                  </div>
                </div>
              </div>

              {/* Business CTA section */}
              <div className={clsx("max-w-4xl mx-auto", isCompact ? "mt-4" : "mt-10")}>
                <div className={clsx(
                  "rounded-2xl bg-gray-800/30 ring-1 ring-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between",
                  isCompact ? "p-4 gap-3" : "p-6 sm:p-8 gap-6"
                )}>
                  <div className="flex-1">
                    <h3 className={clsx(
                      "font-semibold text-squawk-blue",
                      isCompact ? "text-sm" : "text-base"
                    )}>
                      Part of a business?
                    </h3>
                    <p className={clsx(
                      "mt-1 text-gray-400",
                      isCompact ? "text-xs" : "text-sm"
                    )}>
                      Get a custom trial for your team — tailored coverage and dedicated support.
                    </p>
                  </div>
                  <a
                    href="mailto:sales@newsquawk.com?subject=Newsquawk%20V2%20Access%20request"
                    onClick={() => {
                      const attr = getAttribution();
                      trackSignupIntent({
                        intent_type: "business_inquiry",
                        utm_source: attr?.utm_source,
                        utm_medium: attr?.utm_medium,
                        utm_campaign: attr?.utm_campaign,
                        referrer_domain: attr?.referrer_domain,
                      });
                    }}
                    className={clsx(
                      "shrink-0 rounded-lg bg-white/10 font-semibold text-white ring-1 ring-inset ring-white/10 hover:bg-white/20 transition-colors",
                      isCompact ? "px-4 py-2 text-xs" : "px-5 py-2.5 text-sm"
                    )}
                  >
                    Book a Call →
                  </a>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

export default PricingModal;
